import Vue from "vue";
import {ArrayHelper} from "../helpers/arrayHelper";
import {StringHelper} from "../helpers/stringHelper";
import IModel from '~/interfaces/modelInterfaces'
import {ObjectHelper} from '~/helpers/objectHelper'
import {ISortable} from '~/interfaces/propertyInterfaces'

interface IElementHandlerOptions<T> {
  builderPath?: string | ((item: T) => string)
}

export default class ElementHandler<T extends IModel & Partial<ISortable> = any> {
  constructor(protected model,
              protected containerClass,
              protected collectionKey: string,
              protected options?: IElementHandlerOptions<T>,
              ){
    if (!ObjectHelper.isHash(this.options)) {
      this.options = {}
    }
  }

  get collectionArrayKey(): string {
    return StringHelper.toCamel(this.collectionKey) + 'Array';
  }

  append(type: number | string, position: number = null, json: object = {}) {
    if (position === null || position === undefined) {
      position = this.nextSortOrder()
    }
    return this.create(this.model, type, position, json);
  }

  create(parent, type: number | string, position: number, json: object = {}, selectItem: boolean=true) {
    json['sort_order'] = position;
    json['content_type'] = type;
    const element = this.model.rootModel.createEmbeddedObject[this.collectionKey](this, json);
    element.initialize(this.model.rootModel, parent, type, json);
    element.initializeAfterParentSet();
    if (typeof this.model.initializeEmbedded === 'function') {
      this.model.initializeEmbedded(parent, element, position, json, selectItem);
    }
    Vue.set(parent[this.collectionKey], element.client_id, element);
    this.model.rootModel.saveChangesToStore({
      modelId: element.client_id,
      area: this.builderPath(element)
    });
    return element;
  }

  createForParentId(parentId: string, type: number | string, position: number, json: object = {}) {
    const parent = this.model.rootModel.findInPropOfType(this.collectionArrayKey, this.containerClass, parentId);
    const element = this.create(parent, type, position, json);
    element.repo.add(element);
    element.select();
    return element;
  }

  move(elementId: string, parentId: string, newPosition: number): void {
    const element = this.model.findInProp(this.collectionArrayKey, elementId);
    const newParent = this.model.findInPropOfType(this.collectionArrayKey, this.containerClass, parentId);
    const oldParent = element.parent;

    if (newParent === oldParent) {
      element.sort_order = newPosition;  // same parent
    } else {
      oldParent.trackCreationDeletion(element.client_id, element.asJson(), null, element);

      element.sort_order = newPosition;
      newParent.trackCreationDeletion(element.client_id, null, element.asJson(), element);
    }
    this.updateElementPositions(newParent, element, newPosition);

    if (newParent !== oldParent) {
      Vue.delete(oldParent[element.rootName], element.client_id);
      element.parent = newParent;
      Vue.set(newParent[element.rootName], element.client_id, element);
    }

    this.model.rootModel.saveChangesToStore();
  }

  moveToCell(elementId, parentId, newPosition) {
    const element = this.model.findInProp(this.collectionArrayKey, elementId);
    if (parentId === element.parent.client_id) {
      element.sort_order = newPosition; // same parent
    } else {
      this.move(elementId, parentId, newPosition);
    }

    this.model.rootModel.saveChangesToStore();
  }

  swapElementPositions(elementId1, elementId2) {
    const element1 = this.model.findInProp(this.collectionArrayKey, elementId1);
    const element2 = this.model.findInProp(this.collectionArrayKey, elementId2);
    [element1.sort_order, element2.sort_order] = [element2.sort_order, element1.sort_order];
    this.model.rootModel.saveChangesToStore();
  }

  updateElementPositions(parent, movedElement, position: number) {
    ArrayHelper.updatePositionsByProp(parent[this.collectionArrayKey], movedElement, position, 'sort_order');
  }

  recalculateElementPositions() {
    this.all
      .sort((a, b) => a.sort_order - b.sort_order)
      .forEach((element, i) => {
        element.sort_order = i;
      });
  }

  lastSortOrder(): number {
    const tmp = [];
    const collection = this.all
    Object.keys(collection).forEach(key => {
      tmp.push(collection[key].sort_order);
    });
    return tmp.sort((a, b) => b - a)[0]
  }

  nextSortOrder(): number {
    const last = this.lastSortOrder();
    return (last || last === 0) ? last + 1 : 0;
  }

  get all(): T[] {
    return this.model[this.collectionArrayKey] || this.defaultAll;
  }

  private get defaultAll(): T[] {
    return ObjectHelper.toArray(this.model[this.collectionKey]);
  }

  find(clientIdOrId: string): T {
    return this.all.find(x => x.id === clientIdOrId || x.client_id === clientIdOrId)
  }

  findBy(callback): T {
    return this.all.find(callback)
  }

  forEach(callbackfn: (value: T, index: number, array: T[]) => void, options?: { recurse?: boolean }): void {
    const handlerKey: string = this.collectionKey + 'Handler'
    this.all.forEach((v, i, a) => {
      callbackfn(v, i, a)
      if (options?.recurse) {
        const handler: ElementHandler<T> = v[handlerKey]
        if (handler) handler.forEach(callbackfn, options)
      }
    })
  }

  map<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[] {
    return this.all.map(callbackfn)
  }

  get isPresent(): boolean {
    return ArrayHelper.isPresent(this.all)
  }

  get isEmpty(): boolean {
    return !this.isPresent
  }

  get last(): T {
    return this.all[this.all.length - 1]
  }

  get first(): T{
    return this.all[0]
  }

  private builderPath(element: T): string {
    if (this.options.builderPath instanceof Function) {
      return this.options.builderPath(element)
    } else {
      return this.options.builderPath
    }
  }
}